import { useLogout } from "@/actions/user.actions";
import { useAuthStore } from "@/states";
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { getAdminTenantContextHeaders } from "@/lib/adminTenantContext";
import { useLayoutEffect } from "react";

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};


/**
 * Registers global axios auth interceptors for access token injection and refresh retries.
 */
const useAxiosInterceptor = (enabled = true) => {
  const { refetch: logout } = useLogout();
  const setToken = useAuthStore((state) => state.setToken);

  useLayoutEffect(() => {
    if (!enabled) return;
    const reqInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token && config.headers && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        if (config.headers) {
          Object.assign(config.headers, getAdminTenantContextHeaders());
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as RetriableRequestConfig | undefined;

        if (
          !originalRequest ||
          originalRequest._skipAuthRefresh ||
          originalRequest.url?.includes("/api/auth/login") ||
          originalRequest.url?.includes("/api/auth/verify-mfa") ||
          originalRequest.url?.includes("/api/auth/admin-mfa/") ||
          originalRequest.url?.includes("/api/auth/refresh") ||
          originalRequest.url?.includes("/api/auth/logout")
        ) {
          return Promise.reject(error);
        }

        const responseData = error.response?.data as any;
        const isIntegrationError =
          responseData &&
          (responseData.code === "TOKEN_REAUTH_REQUIRED" ||
            responseData.error === "TOKEN_REAUTH_REQUIRED" ||
            responseData.error === "TOKEN_EXPIRED_OR_INVALID");

        if (error.response?.status === 401 && !isIntegrationError && !originalRequest._retry) {
          // During impersonation, skip token refresh — the banner timer handles session exit.
          // The refresh token cookie belongs to the super admin, so refreshing would break context.
          const isImpersonating = Boolean(localStorage.getItem("authSnapshot"));
          if (isImpersonating) {
            return Promise.reject(error);
          }

          // Standard (non-impersonation) refresh flow
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axios(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const { data } = await axios.post(
              "/api/auth/refresh",
              {},
              { withCredentials: true, _retry: true, _skipAuthRefresh: true } as RetriableRequestConfig,
            );

            const newAccessToken = data.token;
            setToken(newAccessToken);
            processQueue(null, newAccessToken);

            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            await logout();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, [enabled, logout, setToken]);

  return null;
};

export default useAxiosInterceptor;
